import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

// "Por que deu diferença?" — compara o mês da tela com o anterior, por pessoa
// (VA, Home Office e VT, 20/10/2026). Busca o mês anterior na MESMA API da
// tela e separa a diferença do total em: quem entrou, quem saiu e quem mudou
// de valor. A soma das três partes fecha com a diferença dos totais.
//
// props:
//   url, params    → GET do mês anterior (params recebe { mes } por cima)
//   mes            → 'AAAA-MM' da tela
//   atual          → resposta da API do mês da tela (o que a página já carregou)
//   extrair(dados) → [{ id, nome, valor, dias, admissao, desligado, motivo }]
//   rotulo         → 'VA' | 'Home Office' | 'VT'

const brl = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const sinal = (v) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${brl(Math.abs(v))}`;
const corSinal = (v) => (v > 0 ? '#116b47' : v < 0 ? '#a11d2b' : '#6c757d');

const mesAnteriorDe = (mes) => {
  const [a, m] = (mes || '').split('-').map(Number);
  if (!a || !m) return null;
  return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, '0')}`;
};
const rotuloMes = (mes) => (mes ? `${mes.slice(5)}/${mes.slice(0, 4)}` : '');

const DiferencaMesAnterior = ({ url, params, mes, atual, extrair, rotulo }) => {
  const mesAnt = mesAnteriorDe(mes);
  const [anterior, setAnterior] = useState(null);
  const [erro, setErro] = useState('');
  const [aberto, setAberto] = useState(true);

  useEffect(() => {
    if (!url || !mesAnt) return;
    setAnterior(null);
    setErro('');
    axios.get(url, {
      params: { ...(params || {}), mes: mesAnt },
      headers: { Authorization: `Bearer ${localStorage.getItem('access')}` },
    })
      .then((res) => setAnterior(res.data))
      .catch(() => setErro('Não foi possível carregar o mês anterior'));
    // params é um objeto novo a cada render: a chave é url + mês
  }, [url, mesAnt]); // eslint-disable-line react-hooks/exhaustive-deps

  const comp = useMemo(() => {
    if (!atual || !anterior) return null;
    // só quem RECEBE no mês (valor > 0) conta como "está na conta"
    const porId = (lista) => {
      const m = new Map();
      (lista || []).forEach((p) => {
        if (!p.id) return;
        const x = m.get(p.id);
        if (x) { x.valor += p.valor || 0; x.dias = Math.max(x.dias || 0, p.dias || 0); }
        else m.set(p.id, { ...p, valor: p.valor || 0 });
      });
      return m;
    };
    const at = porId(extrair(atual));
    const an = porId(extrair(anterior));
    const recebe = (p) => p && p.valor > 0.004;
    const entraram = [];
    const sairam = [];
    const mudaram = [];
    new Set([...at.keys(), ...an.keys()]).forEach((id) => {
      const a = at.get(id);
      const b = an.get(id);
      if (recebe(a) && !recebe(b)) entraram.push({ ...a, dif: a.valor });
      else if (!recebe(a) && recebe(b)) {
        // por que saiu: o que a tela do mês atual diz da pessoa (desligado,
        // férias, sem dias...) ou que ela nem está mais na lista
        const motivo = !a ? 'não está mais na lista'
          : a.desligado ? 'desligado(a)'
            : (a.motivo || 'sem valor neste mês');
        sairam.push({ ...b, motivo, dif: -b.valor });
      } else if (recebe(a) && recebe(b) && Math.abs(a.valor - b.valor) > 0.004) {
        mudaram.push({ ...a, valorAnt: b.valor, diasAnt: b.dias, dif: a.valor - b.valor });
      }
    });
    const porNome = (x, y) => (x.nome || '').localeCompare(y.nome || '', 'pt-BR');
    entraram.sort(porNome); sairam.sort(porNome);
    mudaram.sort((x, y) => Math.abs(y.dif) - Math.abs(x.dif));
    const soma = (l) => l.reduce((s, x) => s + x.dif, 0);
    const totAt = [...at.values()].reduce((s, x) => s + (recebe(x) ? x.valor : 0), 0);
    const totAn = [...an.values()].reduce((s, x) => s + (recebe(x) ? x.valor : 0), 0);
    return {
      entraram, sairam, mudaram, totAt, totAn,
      qtdAt: [...at.values()].filter(recebe).length,
      qtdAn: [...an.values()].filter(recebe).length,
      somaE: soma(entraram), somaS: soma(sairam), somaM: soma(mudaram),
    };
  }, [atual, anterior, extrair]);

  if (!mesAnt) return null;
  const difTotal = comp ? comp.totAt - comp.totAn : 0;

  const Bloco = ({ titulo, icone, cor, lista, soma, colunas }) => (
    lista.length === 0 ? null : (
      <div className="mb-3">
        <div className="fw-bold small mb-1" style={{ color: cor }}>
          <i className={`fas ${icone} me-2`}></i>{titulo} ({lista.length})
          <span className="ms-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{sinal(soma)}</span>
        </div>
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <tbody>
              {lista.map((p) => (
                <tr key={p.id}>
                  <td className="fw-semibold" style={{ width: '35%' }}>{p.nome}</td>
                  <td className="small text-muted">{colunas(p)}</td>
                  <td className="text-end fw-semibold"
                    style={{ color: corSinal(p.dif), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {sinal(p.dif)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  );

  return (
    <div className="bg-white rounded shadow-sm p-3 mt-3" style={{ borderLeft: '4px solid #00b5cc' }}>
      <button type="button" className="btn btn-link p-0 fw-bold text-decoration-none"
        style={{ color: '#00205b' }} onClick={() => setAberto((v) => !v)}>
        <i className={`fas fa-chevron-${aberto ? 'down' : 'right'} me-2`}></i>
        Diferença para {rotuloMes(mesAnt)} — quem entrou, quem saiu e quem mudou no {rotulo}
      </button>
      {aberto && (
        <div className="mt-2">
          {erro ? (
            <div className="small text-muted">{erro}</div>
          ) : !comp ? (
            <div className="small text-muted">
              <span className="spinner-border spinner-border-sm me-2"></span>Comparando com {rotuloMes(mesAnt)}...
            </div>
          ) : (
            <>
              <div className="d-flex flex-wrap gap-3 align-items-center mb-3 small">
                <span>{rotuloMes(mesAnt)}: <b>{brl(comp.totAn)}</b> ({comp.qtdAn} pessoa(s))</span>
                <i className="fas fa-arrow-right text-muted"></i>
                <span>{rotuloMes(mes)}: <b>{brl(comp.totAt)}</b> ({comp.qtdAt} pessoa(s))</span>
                <span className="badge fs-6" style={{ background: '#f8f9fa', color: corSinal(difTotal), border: '1px solid #dee2e6' }}>
                  Diferença {sinal(difTotal)}
                </span>
              </div>
              {comp.entraram.length + comp.sairam.length + comp.mudaram.length === 0 ? (
                <div className="small text-muted">
                  <i className="fas fa-check me-2"></i>Mesmas pessoas e mesmos valores do mês anterior.
                </div>
              ) : (
                <>
                  <Bloco titulo="Entraram" icone="fa-user-plus" cor="#116b47" lista={comp.entraram}
                    soma={comp.somaE}
                    colunas={(p) => [p.admissao && `admissão ${p.admissao}`, p.dias != null && `${p.dias} dia(s)`]
                      .filter(Boolean).join(' · ') || 'não recebeu no mês anterior'} />
                  <Bloco titulo="Saíram" icone="fa-user-minus" cor="#a11d2b" lista={comp.sairam}
                    soma={comp.somaS}
                    colunas={(p) => `${p.motivo} · recebia ${brl(p.valor)}`} />
                  <Bloco titulo="Mudaram de valor" icone="fa-arrows-rotate" cor="#8a6d00" lista={comp.mudaram}
                    soma={comp.somaM}
                    colunas={(p) => `${brl(p.valorAnt)} → ${brl(p.valor)}${p.dias != null && p.diasAnt != null && p.dias !== p.diasAnt
                      ? ` · dias ${p.diasAnt} → ${p.dias}` : ''}`} />
                  <div className="small text-muted border-top pt-2">
                    Entraram {sinal(comp.somaE)} + saíram {sinal(comp.somaS)} + mudaram {sinal(comp.somaM)}
                    {' '}= <b style={{ color: corSinal(difTotal) }}>{sinal(comp.somaE + comp.somaS + comp.somaM)}</b>
                    {' '}— a diferença do total. Conta quem tem valor no mês, sem filtros da tela.
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DiferencaMesAnterior;
